import { makeAutoObservable, autorun } from 'mobx'
import { storeMobx, cartMobx, feeMobx, restaurantCenter, RESTAURANT_EVENT } from 'restaurant'
import { memberMobx, decorationMobx, timePickMobx } from '@mobxStore'
import { mallCartMobx } from 'mobx/mall'
import { sortBy, find, some, cloneDeep, uniqBy, forEach, map, keyBy } from '@dt/fe-utils/dist/lodash'
import { getDistance, coordtransform } from '@dt/fe-utils'

class DeliveryMobx {
  constructor () {
    makeAutoObservable(this)
    restaurantCenter.on(RESTAURANT_EVENT.EAT_TYPE_CHANGE, (data) => {
      if (data.eatType === 'delivery' && !cartMobx.info.contactAddress && deliveryMobx.hasAddresses) {
        deliveryMobx.availableAddress && deliveryMobx.setAddress(deliveryMobx.availableAddress)
      }
    })
  }

  address: any // 收货地址

  showModalFlag: boolean // 弹窗标记

  /** 记录当前地址去校验门店列表的可配送情况 */
  addressStoreList: IAddressInAreaStoreInfo[] = []

  get storeIdToaddressMap () {
    return keyBy(this.addressStoreList, 'storeId')
  }

  get isDelivery () {
    return cartMobx.info.eatType === 'delivery'
  }

  get hasAddresses () {
    return !!memberMobx.addressList?.length
  }

  get viewAddresses () {
    const addressList = memberMobx.addressList?.map(address => {
      return { ...address }
    })
    return this.verifyAddresses(addressList)
  }

  get viewAddressesAll () {
    return memberMobx.addressList?.map(address => {
      return { ...address }
    })
  }

  get nearestArea () {
    const { deliveryArea } = storeMobx.info?.selfDeliveryConfig || {}
    if (deliveryArea && deliveryArea.length) {
      let isSameExpressFee = uniqBy(deliveryArea, 'expressFee')?.length === 1
      let isSameDeliveryInTime = uniqBy(deliveryArea, 'deliveryInTime')?.length === 1
      let deliveryAreas = sortBy(deliveryArea, item => item.deliveryDistance - 0)
      if (isSameExpressFee) {
        deliveryAreas = sortBy(deliveryArea, item => item.deliveryInTime - 0)
        if (isSameDeliveryInTime) {
          deliveryAreas = sortBy(deliveryArea, item => item.deliveryDistance - 0)
        }
      }
      let inArea = find(deliveryAreas, item => {
        return this.inArea(this.address, item.areas)
      })
      return inArea
    }
    return null
  }

  get expressFee () {
    return this.nearestArea ? this.nearestArea.expressFee - 0 || 0 : 0
  }

  get expressTime () {
    return this.nearestArea ? this.nearestArea.deliveryInTime - 0 || 0 : 0
  }

  get leastFee () {
    const leastFee = (this.nearestArea?.hasOwnProperty('leastFee') ? this.nearestArea?.leastFee : storeMobx.info?.selfDeliveryConfig?.leastFee) || 0
    return leastFee - 0
  }

  get availableAddress () {
    const store = storeMobx.info
    /** 如果当前地址还是在配送范围，就不切换 */
    if (this.address?.memberAddressId) {
      let nowAddress = this.viewAddresses?.find(address => address.memberAddressId === this.address?.memberAddressId && address.inCityId && address.inArea)
      if (nowAddress) {
        return nowAddress
      } else {
        let otherAddress = this.viewAddresses?.find(address => address.inCityId && address.inArea)
        return otherAddress
      }
    } else if (store.storeId && this.viewAddresses?.length && !this.address?.memberAddressId) {
      let address = this.viewAddresses.find(address => address.inCityId && address.inArea)
      return address
    }
    return null
  }

  get isShowModal () {
    if (Config.token && memberMobx.memberInfo.memberId === 0) {
      return true
    }
    return this.showModalFlag
  }

  /** 选中地址 */
  setAddress (info?: any, type: string = '') {
    this.address = info
    const { realName, mobile, lng, lat, lbsType, province = '', city = '', district = '', address, addressDetail, cityId, extra } = info || {}
    const _province = province || ''
    const _city = city || ''
    const _address = `${_province}${_city}${district?.replace(_province, '')?.replace(_city, '')}${address}${addressDetail}`
    if (type === 'mall') {
      mallCartMobx.setAddressInfo({
        contactName: realName,
        contactMobile: mobile,
        contactAddress: _address,
        contactInfo: {
          extra,
          city: _city,
          province: _province,
          lbsType: lbsType,
          latitude: String(lat),
          longitude: String(lng),
          address: _address,
          middleAddress: `${_province}${!district?.includes(_city) ? _city : ''}${district}`, // 省市区路
          shortAddress: address + addressDetail
        }
      })
    } else {
      cartMobx.setCityId(cityId)
      cartMobx.setExpressPrice(this.expressFee)
      // cartMobx.setExpressList()
      cartMobx.setDeliveryAddressInfo('delivery', {
        contactName: realName,
        contactMobile: mobile,
        contactAddress: _address,
        contactInfo: {
          deliveryType: 'selfDelivery',
          lbsType: lbsType,
          latitude: String(lat),
          longitude: String(lng),
          address: _address,
          shortAddress: address + addressDetail
        }
      })
      if (!this.expressTime || this.expressTime !== cartMobx.info.deliveryInTime) {
        timePickMobx.reset()
        // 补偿，切换地址后，需要更新配送费startTime
        setTimeout(() => {
          cartMobx.setExpressList()
        }, 1000)
      }
      cartMobx.setDeliveryInTime(this.expressTime)
      restaurantCenter.setConfig('addressLbs', { lat, lng })
    }
  }

  /** 重置地址 */
  resetAddress () {
    this.address = undefined
    cartMobx.setCityId()
    cartMobx.setExpressPrice()
    cartMobx.setExpressList()
    cartMobx.setDeliveryInTime()
    mallCartMobx.setAddressInfo()
    cartMobx.setDeliveryAddressInfo(cartMobx.info.eatType || Config.eatType || decorationMobx.defaultEatType)
  }

  /** 判断是否在范围里 */
  inArea (point, area = []) {
    if (!(point || this.address)) return false
    point = cloneDeep(point || this.address)
    point.lng = Number(point.lng)
    point.lat = Number(point.lat)
    let i, j = area.length - 1
    let check = false
    for (i = 0; i < area.length; i++) {
      if ((area[i].lng < point.lng && area[j].lng >= point.lng || area[j].lng < point.lng && area[i].lng >= point.lng) && (area[i].lat <= point.lat || area[j].lat <= point.lat)) {
        if (area[i].lat + (point.lng - area[i].lng) * (area[j].lat - area[i].lat) / (area[j].lng - area[i].lng) < point.lat) {
          check = !check
        }
      }
      j = i
    }
    return check
  }

  /** 判断是否在一些范围里 */
  inAreas (point, datas) {
    return some(datas, data => this.inArea(point, data.areas))
  }

  /** 校验地址是否可用 */
  verifyAddress () {
    const { deliveryArea } = storeMobx.info?.selfDeliveryConfig || {}
    const { contactInfo, cityId } = cartMobx.info
    const { taxMapConfig } = feeMobx
    const point = {
      lat: contactInfo?.latitude,
      lng: contactInfo?.longitude
    }
    const inArea = this.inAreas(point, deliveryArea)
    const inCityId = bo === 'cn' || taxMapConfig[cityId]
    if (!inArea || !inCityId) {
      this.resetAddress()
      return false
    }
    return true
  }

  /** 校验地址是否在门店配送范围 storeInfo自定义门店去筛选地址 */
  verifyAddresses (addressList, storeInfo: IStore = storeMobx.info) {
    const { deliveryArea } = storeInfo?.selfDeliveryConfig || {}
    const { taxMapConfig } = feeMobx
    if (deliveryArea && deliveryArea.length && addressList?.length) {
      addressList.forEach((address) => {
        // 地址是否在配送范围
        address.inArea = this.inAreas(address, deliveryArea)
        // 地址是否配税
        address.inCityId = bo === 'cn' || taxMapConfig[address.cityId]
      })
    }
    return addressList
  }

  /** 校验选中的地址门店列表的配送范围 */
  calcStoresInAddress (address = this.address) {
    const { taxMapConfig } = feeMobx
    const list = map(storeMobx.storeList, (storeInfo) => {
      const { deliveryArea } = storeInfo?.selfDeliveryConfig || {}
      return {
        storeId: storeInfo.storeId,
        // 地址是否在配送范围
        inArea: this.inAreas(address, deliveryArea),
        // 地址是否配税
        inCityId: bo === 'cn' || taxMapConfig[address.cityId],
      }
    })
    this.addressStoreList = list
    return this.addressStoreList
  }

  /** 获取最近的门店 */
  getNearByStore ({ address, notStoreId }: { address: any; notStoreId: number }) {
    let storeList = storeMobx.viewStoreList
      .filter(store => !!store.openSelfDelivery && store.storeId !== notStoreId)
      .map(store => {
        let lbsLat = store.lbsLat
        let lbsLng = store.lbsLng
        if (store.lbsType === 'google') {
          const lbsB = coordtransform.bd09towgs84(store.lbsLat, store.lbsLng)
          lbsLat = lbsB[0]
          lbsLng = lbsB[1]
        }
        return {
          ...store,
          distance: getDistance(Number(address.lat), Number(address.lng), lbsLat, lbsLng)
        }
      })
    storeList = sortBy(storeList, 'distance')
    const storeInfo = find((storeList || []), (storeInfo) => storeInfo && storeInfo.selfDeliveryConfig?.deliveryArea && this.inAreas(address, storeInfo.selfDeliveryConfig.deliveryArea) && address.inCityId)
    return storeInfo
  }

  /** 编辑地址弹窗标记 */
  setShowModalFlag () {
    this.showModalFlag = true
  }

  setLbsAddressInfo () {

  }
}

const deliveryMobx = new DeliveryMobx()

autorun(() => {
  if (deliveryMobx.address && deliveryMobx.address?.memberAddressId && !deliveryMobx.viewAddresses.find(address => address.memberAddressId === deliveryMobx.address?.memberAddressId)) {
    deliveryMobx.resetAddress()
  }
})

export default deliveryMobx
